import { Button, Divider, Grid, Paper, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { useTranslation } from 'react-i18next';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { getFundraiserSysSettings, updateFundraiserSysSettings } from 'src/shared/services/fundraiserSettingsService';
import { FormProvider, useForm } from 'react-hook-form';
import { FundraiserSettingsAccordionEnum, FundraiserSettingsSectionPrefixEnum } from 'src/shared/models/enums/fundraiserSettingsAccordion.enum';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import { FundraiserSettingsAccordion } from '../components/fundraiserSettingsAccordion';
import { FundraiserSysSettings } from '../models/fundraiserSysSettings';

export const FundraiserSettingsEmailTextOverviewPage = () => {
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const [loading, setLoading] = useState<boolean>(false);

  const formMethods = useForm<FundraiserSysSettings>({
    mode: 'all',
    defaultValues: {
      generalGift: {
        newDonationNotice: { subject: '', body: '' },
        postPaymentText: { heading: '', ingress: '', returnUrl: '' },
        receiptToDonor: { subject: '', body: '' },
        giftLetterToDonor: { subject: '', body: '' },
      },
      celebration: {
        newDonationNotice: { subject: '', body: '' },
        postPaymentText: { heading: '', ingress: '', returnUrl: '' },
        receiptToDonor: { subject: '', body: '' },
        giftLetterToDonor: { subject: '', body: '' },
      },
      memorial: {
        newDonationNotice: { subject: '', body: '' },
        postPaymentText: { heading: '', ingress: '', returnUrl: '' },
        receiptToDonor: { subject: '', body: '' },
        giftLetterToDonor: { subject: '', body: '' },
      },
    },
  });

  const {
    reset,
    watch,
    handleSubmit,
    formState: { isDirty },
  } = formMethods;
  ConsoleHelper.log('watchForm:: ', watch());

  const fetchData = useCallback(async () => {
    if (isMounted()) {
      setLoading(true);
    }
    try {
      const result = await getFundraiserSysSettings();
      if (isMounted()) {
        reset(result.data);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
      }
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  }, [isMounted, reset]);

  useEffect(() => {
    if (isMounted()) {
      fetchData();
    }
  }, [fetchData, isMounted]);

  const onSubmit = useCallback(
    async (data: FundraiserSysSettings) => {
      if (isMounted()) {
        setLoading(true);
      }
      try {
        setLoading(true);
        await updateFundraiserSysSettings(data);
        await fetchData();
      } catch (error) {
        ConsoleHelper.log('Error updating fundraiser settings:', error);
      } finally {
        setLoading(false);
      }
    },
    [fetchData, isMounted],
  );

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <GridWrapper>
          <LoadingOverlay loading={loading} />
          <Grid item xs={12}>
            <Paper>
              <Grid container sx={{ p: 2.5 }}>
                <Grid item xs={12}>
                  <Typography variant='h6' sx={{ fontWeight: 700 }}>
                    {t('fundraiser:fundraiserType.general')}
                  </Typography>
                  <FundraiserSettingsAccordion
                    prefix={FundraiserSettingsSectionPrefixEnum.GeneralGift}
                    excludeSection={[FundraiserSettingsAccordionEnum.GiftLetterToDonor]}
                  />
                  <Divider sx={{ my: 1 }} />
                </Grid>

                <Grid item xs={12} mt={5}>
                  <Typography variant='h6' sx={{ fontWeight: 700 }}>
                    {t('fundraiser:fundraiserType.memorial')}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <FundraiserSettingsAccordion prefix={FundraiserSettingsSectionPrefixEnum.Memorial} />
                </Grid>

                <Grid item xs={12} mt={5}>
                  <Typography variant='h6' sx={{ fontWeight: 700 }}>
                    {t('fundraiser:fundraiserType.celebration')}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <FundraiserSettingsAccordion prefix={FundraiserSettingsSectionPrefixEnum.Celebration} />
                </Grid>
              </Grid>
              <Grid item xs={12} mt={3} display='flex' justifyContent='flex-end'>
                <Button size='medium' variant='contained' sx={{ mr: 2, mb: 2 }} disabled={loading || !isDirty} type='submit'>
                  {t('common:button.save')}
                </Button>
              </Grid>
            </Paper>
          </Grid>
        </GridWrapper>
      </form>
    </FormProvider>
  );
};
